import { Ulid } from "@kase/my-marathon-communication";
import { computed, signal } from "@preact/signals";
import { Track, TrackGroup } from "../api/time/interfaces";
import type {
    ExternalTile,
    MakeTrackerParams,
    TaskTile,
    TrackGroupTile,
} from "./interfaces";

const makeTracker = ({ api }: MakeTrackerParams) => {
    const history = signal<TaskTile[]>([]);
    const tasks = computed(() => {
        const tracks = api.time.track.tracks.value;
        const result = history.value.map((tile) => {
            const track = tracks.find((item) => item.id === tile.id);

            if (track) {
                return viewTask(track);
            }

            return tile.state === "idle"
                ? tile
                : { ...tile, state: "idle" as const };
        });

        for (const track of tracks) {
            if (
                track.kind?.$case === "task" &&
                track.stop === 0 &&
                !result.some((tile) => tile.id === track.id)
            ) {
                result.push(viewTask(track));
            }
        }

        return result;
    });
    const tiles = computed(() => {
        const trackGroups = api.time.group.trackGroups.value.filter(
            (tg) => tg.active,
        );
        const tracks = api.time.track.tracks.value;

        return trackGroups.map((group) => viewTile(group, tracks));
    });
    const activeExternal = computed(() => {
        const active = api.time.track.tracks.value.find(
            (track) =>
                track.kind?.$case === "calendar_external" && track.stop === 0,
        );

        return active ? viewExternal(active) : null;
    });
    const activeTask = computed(() =>
        tasks.value.find((task) => task.state === "in_progress"),
    );
    const activeTile = computed(() =>
        tiles.value.find((tile) => tile.state === "in_progress"),
    );
    const loaded = signal(false);
    const loading = signal(false);

    const viewTask = (track: Track): TaskTile => ({
        type: "task",
        id: track.id.toString(),
        state: track.stop === 0 ? "in_progress" : "idle",
        name: track.task?.name ?? "",
        startDate: new Date(track.start),
        _task: track.task?._id ?? null,
        _track: track._id,
    });

    const viewTile = (group: TrackGroup, tracks: Track[]): TrackGroupTile => {
        const activeTrack = tracks.find(
            (item) =>
                item.kind?.$case === "track_group" &&
                item.kind.track_group.equals(group._id) &&
                item.stop === 0,
        );

        return {
            type: "track_group",
            id: group.id,
            _track: activeTrack?._id ?? null,
            _trackGroup: group._id,
            name: group.name,
            startDate: activeTrack ? new Date(activeTrack.start) : null,
            state: activeTrack ? "in_progress" : "idle",
            inactive:
                group.project?.active === false ||
                group.feeCode?.active === false ||
                group.feeCategory?.active === false,
        };
    };

    const viewExternal = (track: Track): ExternalTile => ({
        type: "external",
        id: track.id.toString(),
        name: track.external?.title ?? "<unknown>",
        startDate: new Date(track.start),
        _track: track._id,
    });

    const load = async () => {
        if (loaded.value || loading.value) {
            return;
        }

        loading.value = true;

        try {
            const { time } = api;
            // @TODO: load tasks?

            await time.group.whenLoaded();

            loaded.value = true;
        } catch (error) {
            console.log("[tracker:load]: error", error);
        } finally {
            loading.value = false;
        }
    };

    const startTask = async (id: Readonly<Ulid>) => {
        const { time, connection, status, task: taskApi } = api;
        const con = connection.peek();

        if (!con) {
            throw new Error("No connection");
        }

        const track = await time.track.startTask(id);
        const taskUser = track.task?.users.value.find((item) =>
            item._assignee?.equals(con.user.user_id),
        );

        if (taskUser) {
            const result = await con.task.user.update({
                etag: taskUser.etag,
                id: taskUser._id,
                status: status.IN_PROGRESS._id,
            });

            await taskApi.loadTaskUser(result);
        }

        history.value = [...history.value, viewTask(track)];
    };

    const startTile = async (id: Readonly<Ulid>) => {
        await api.time.track.startGroup(id);
    };

    const stop = async (id: Readonly<Ulid>) => {
        const { time, connection, status, task: taskApi } = api;
        const con = connection.peek();

        if (!con) {
            throw new Error("No connection");
        }

        const track = await time.track.stopTrack(id);

        if (track.kind?.$case !== "task") {
            return;
        }

        //
        // task status change works only for task tracks
        //

        const oldTasks = history.value.filter((task) => task.id !== track.id);
        const taskUser = track.task?.users.value.find((item) =>
            item._assignee?.equals(con.user.user_id),
        );

        if (taskUser) {
            try {
                const result = await con.task.user.update({
                    etag: taskUser.etag,
                    id: taskUser._id,
                    status: status.IDLE._id,
                });

                await taskApi.loadTaskUser(result);
            } catch (err) {
                console.log("error happened", err);
            }
        }

        history.value = [...oldTasks, { ...viewTask(track), state: "idle" }];
    };

    return {
        tasks,
        activeTask,
        tiles,
        activeTile,
        activeExternal,
        loading,
        load,
        startTask,
        startTile,
        stop,
    };
};

export default makeTracker;
